import Box from "@mui/material/Box";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import { Avatar, Typography, useMediaQuery } from "@mui/material";
import { TableCellIconStyled, TableCellName, CardFirstSectionContainer } from "./brawlers-stats-table-card.styles";
import React from "react";
import { componentColors, screenBreackpoints } from "../../theme";
import { format } from "date-fns/format";
import { useTranslation } from "react-i18next";
import { CardContainer } from "../base-components/base-componets";
import { getUrlWithLang, nameToUrlFormatted } from "../../routes/routes.functions";
import { useLanguage } from "../../hooks/language/language";
import { APP_ROUTES } from "../../routes/routes.constants";
import { TableSort } from "../table-sort/table-sort";
import { Column } from "../table-sort/table-sort.types";
import { RowLink, TableCellStyled } from "../table-sort/table-sort.styles";
import { SharePage } from "../share-page/share-page";
import { formatPercentaje, formatScore, getBrawlerImgSrc, ImageType } from "../../shared/functions";
import { BrawlerStats } from "../../api/domine/brawler-stats.types";
import { Timestamp } from "../../domine/timestamp.types";
import { HighlightedTypography, Highlight } from "../highlighted-typography/highlighted-typography";

interface Props {
  brawlersStats: BrawlerStats[];
  lastUpdate: Timestamp
  title: string
  highlights: Highlight[],
  shareElement: string,
  shareText: string,
  nOptionVisible: number
  showTotalBattles?: boolean
}

export const BrawlersStatsTableCard: React.FC<Props> = ({
  brawlersStats,
  lastUpdate,
  title,
  highlights,
  shareElement,
  shareText,
  showTotalBattles = true,
  nOptionVisible,
}: Props) => {
  const isMobile = useMediaQuery(`(max-width:${screenBreackpoints.mobile})`);
  const totalBattles = Math.round(brawlersStats.reduce((accumulator: number, brawler) => accumulator + brawler.totalBattles, 0) / 2);
  const { t } = useTranslation();
  const { getLanguage } = useLanguage();
  const language = getLanguage()
  const columns: Column<BrawlerStats>[] = [
    { title: "Avatar", key: "brawlerId", empty: true },
    { title: "Brawler", key: "brawlerName" },
    { title: "Wins %", key: "winRate" },
    { title: "Use %", key: "useRate" },
    { title: "Score", key: "effectivityRate", tooltip: "High win rate and low score is a situational brawler" },
  ]

  return (
    <Box>
      <CardContainer>
        <CardFirstSectionContainer isMobile={isMobile}>
          <HighlightedTypography
            text={title}
            highlights={highlights}
            component="h2"
            fontSize={"1.2em"}
          />
          <Typography component="span" fontSize={"1em"}>
            {showTotalBattles &&
              t("COMPONENTS.TABLE_BRAWLERS_BATTLES.TOTAL_BATTLES", {
                totalBattles: totalBattles.toLocaleString()
              }) + " "}
            {t("COMPONENTS.TABLE_BRAWLERS_BATTLES.UPDATED_AT", {
              updatedAt: format(new Date(lastUpdate.seconds * 1000), 'dd/MM/yyyy')
            })}
          </Typography>
          <TableSort
            data={brawlersStats}
            columns={columns}
            sortDefault={"effectivityRate"}
            nOptionVisible={nOptionVisible}
            renderBody={(visibleData) => (
              <TableBody>
                {visibleData.map((brawlerStats) => (
                  <TableRow component={RowLink} key={brawlerStats.brawlerId}
                    to={getUrlWithLang(language, `${APP_ROUTES.BRAWLER}/${nameToUrlFormatted(brawlerStats.brawlerName)}`)}
                    aria-label={brawlerStats.brawlerName}>
                    <TableCellIconStyled isMobile={isMobile}>
                      <Avatar src={getBrawlerImgSrc(ImageType.AVATAR, brawlerStats.brawlerName)} alt={`${t("SHARED.AVATAR")} ${brawlerStats.brawlerName}`}>
                        <Avatar src={getBrawlerImgSrc(ImageType.AVATAR)} alt={`${t("SHARED.DEFAULT")} ${t("SHARED.AVATAR")}`} />
                      </Avatar>
                    </TableCellIconStyled>
                    <TableCellName isMobile={isMobile}>
                      {brawlerStats.brawlerName}
                    </TableCellName>
                    <TableCellStyled isMobile={isMobile}>{formatPercentaje(brawlerStats.winRate)}</TableCellStyled>
                    <TableCellStyled isMobile={isMobile}>{formatPercentaje(brawlerStats.useRate)}</TableCellStyled>
                    <TableCellStyled isMobile={isMobile}>{formatScore(brawlerStats.effectivityRate)}</TableCellStyled>
                  </TableRow>
                ))}
              </TableBody>
            )}
          />
        </CardFirstSectionContainer>
        <SharePage
          shareTitle={title + "!"}
          shareChild={
            <HighlightedTypography
              text={shareText}
              highlights={[{ word: shareElement ?? "", color: componentColors.linkText }]}
            />
          }
        />
      </CardContainer>
    </Box>
  );
}
